import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, MapPinIcon, TagIcon, UsersIcon, HeartIcon, CheckCircle2Icon, XCircleIcon, TargetIcon, WalletIcon, MicIcon } from 'lucide-react';
import { CampNavbar } from '../components/CampNavbar';
import { LegalFooter } from '../components/LegalFooter';
const FORM_URL = 'https://api.leadconnectorhq.com/widget/form/8oL7mzyZiV6bC0aIfg98';
function CtaButton({
  children,
  large = false
}: {
  children: React.ReactNode;
  large?: boolean;
}) {
  return <a href={FORM_URL} className={`inline-block text-center bg-brand-accent text-brand-black font-heading font-bold uppercase tracking-industrial rounded-[4px] hover:bg-white transition-all duration-300 accent-glow-hover ${large ? 'px-10 py-6 text-lg' : 'px-8 py-5 text-base'} w-full sm:w-auto`} data-id="element-223">
      {children}
    </a>;
}
const fadeUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function TabaraPage() {
  return <div className="min-h-screen bg-brand-black text-brand-white font-body selection:bg-brand-accent selection:text-brand-black" data-id="element-224">
      <CampNavbar data-id="element-225" />

      <main data-id="element-226">
        {/* SECTION 1 — HERO */}
        <section className="relative min-h-screen flex items-center pt-24 pb-16 bg-brand-black overflow-hidden industrial-texture" data-id="element-227">
          <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=70&auto=format&fit=crop" alt="Peisaj montan" className="absolute inset-0 w-full h-full object-cover opacity-25" data-id="element-228" />
          <div className="absolute inset-0 bg-brand-black/80 z-0" data-id="element-229" />

          <div className="max-w-[1280px] mx-auto px-6 lg:px-20 w-full relative z-10" data-id="element-230">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="flex flex-col items-center text-center lg:grid lg:grid-cols-2 lg:gap-x-12 lg:items-center lg:text-left" data-id="element-231">
              {/* Badge */}
              <div className="order-1 lg:col-start-1 lg:row-start-1 mb-6" data-id="element-232">
                <div className="inline-block border border-brand-accent px-4 py-1.5 rounded-[2px]" data-id="element-233">
                  <span className="font-heading font-bold text-brand-accent text-xs sm:text-sm uppercase tracking-industrial-wide" data-id="element-234">
                    Tabără de 3 zile
                  </span>
                </div>
              </div>

              {/* Title */}
              <h1 className="order-2 lg:col-start-1 lg:row-start-2 font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-brand-white uppercase tracking-industrial mb-6" data-id="element-235">
                Banii bată-i vina... Trei zile despre bani, echilibru și viața
                pe care o vrei
              </h1>

              {/* Photo — right column on desktop, between title and subtitle on mobile */}
              <div className="order-3 lg:col-start-2 lg:row-start-1 lg:row-end-7 w-full mb-8 lg:mb-0 lg:self-stretch lg:flex lg:items-center" data-id="element-236">
                <div className="relative w-full max-w-sm mx-auto lg:max-w-none h-[360px] sm:h-[420px] lg:h-[560px] rounded-lg overflow-hidden border border-brand-border bg-brand-grey" data-id="element-237">
                  <img src="https://cdn.magicpatterns.com/uploads/otREbWCj6RcLspovbRze9j/Screenshot_2026-04-28_at_19.47.45.png" alt="Ionuț Drăgănescu — gazda taberei" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-238" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black/50 to-transparent" data-id="element-239" />
                  <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-brand-accent/40" data-id="element-240" />
                  <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-brand-accent/40" data-id="element-241" />
                </div>
              </div>

              {/* Subtitle */}
              <p className="order-4 lg:col-start-1 lg:row-start-3 font-body text-lg sm:text-xl text-brand-muted leading-relaxed mb-10 max-w-2xl" data-id="element-242">
                O tabără de trei zile la munte pentru antreprenori, părinți și
                oamenii care vor mai multă claritate în viață și în bani. Înveți
                prin joacă, te relaxezi și pleci cu un plan clar.
              </p>

              {/* Event details */}
              <div className="order-5 lg:col-start-1 lg:row-start-4 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 w-full max-w-2xl" data-id="element-243">
                {[{
                icon: <CalendarIcon className="w-5 h-5" data-id="element-244" />,
                text: '24 – 26 iulie 2026 (trei zile)'
              }, {
                icon: <MapPinIcon className="w-5 h-5" data-id="element-245" />,
                text: 'Vlahia Inn, Moieciu de Sus'
              }, {
                icon: <UsersIcon className="w-5 h-5" data-id="element-246" />,
                text: 'Copiii participă gratuit'
              }, {
                icon: <HeartIcon className="w-5 h-5" data-id="element-247" />,
                text: 'Banii merg către o cauză bună'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-brand-grey/60 border border-brand-border rounded-lg px-4 py-3 text-left" data-id="element-248">
                    <span className="text-brand-accent shrink-0" data-id="element-249">
                      {item.icon}
                    </span>
                    <span className="font-body text-brand-white text-sm" data-id="element-250">
                      {item.text}
                    </span>
                  </div>)}
              </div>

              {/* Price */}
              <div className="order-6 lg:col-start-1 lg:row-start-5 flex items-center justify-center lg:justify-start gap-4 mb-8" data-id="element-251">
                <TagIcon className="w-6 h-6 text-brand-accent shrink-0" data-id="element-252" />
                <div className="flex items-baseline gap-3" data-id="element-253">
                  <span className="font-heading font-black text-3xl text-brand-white" data-id="element-254">
                    500 lei
                  </span>
                  <span className="font-body text-brand-dim line-through text-lg" data-id="element-255">
                    1200 lei
                  </span>
                  <span className="font-body text-brand-muted text-sm" data-id="element-256">
                    / persoană
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div className="order-7 lg:col-start-1 lg:row-start-6 flex flex-col items-center lg:items-start gap-4 w-full" data-id="element-257">
                <CtaButton large data-id="element-258">Rezervă-ți locul la tabără</CtaButton>
                <p className="font-body text-sm text-brand-dim max-w-md" data-id="element-259">
                  Locurile sunt limitate. Copiii participă gratuit, iar banii
                  merg către o cauză bună.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — PROBLEM */}
        <section className="py-20 md:py-28 bg-brand-dark" data-id="element-260">
          <div className="max-w-3xl mx-auto px-6 lg:px-20" data-id="element-261">
            <motion.div {...fadeUp} data-id="element-262">
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-brand-white uppercase tracking-industrial mb-10 text-center" data-id="element-263">
                Muncești mult, dar parcă tot nu ai liniște?
              </h2>

              <div className="space-y-5 font-body text-brand-muted text-lg leading-relaxed mb-10" data-id="element-264">
                <p data-id="element-265">
                  Te trezești dimineața cu o listă lungă de griji și pleci la
                  culcare la fel de obosit.
                </p>
                <p data-id="element-266">Câștigi bani, dar nu știi exact unde se duc.</p>
                <p data-id="element-267">
                  Vrei să pui ceva deoparte sau să investești, dar nu ai un plan
                  clar.
                </p>
                <p data-id="element-268">
                  Munca îți mănâncă timpul pe care ai vrea să îl petreci cu
                  copiii tăi.
                </p>
                <p data-id="element-269">
                  Când trebuie să vorbești în fața oamenilor, simți cum ți se
                  strânge stomacul.
                </p>
                <p data-id="element-270">
                  Și undeva în spate e gândul că vrei o viață mai echilibrată,
                  dar nu știi de unde să începi.
                </p>
              </div>

              <div className="bg-brand-grey border-l-4 border-brand-accent rounded-r-lg p-6 mb-10" data-id="element-271">
                <p className="font-sub italic text-brand-white text-lg leading-relaxed" data-id="element-272">
                  Am simțit și noi la fel. De aceea am creat aceste trei zile la
                  munte, unde înveți toate astea prin joacă, fără presiune și
                  fără cursuri plictisitoare.
                </p>
              </div>

              <div className="text-center" data-id="element-273">
                <CtaButton data-id="element-274">Rezervă-ți locul la tabără</CtaButton>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3 — WHAT YOU LEARN */}
        <section className="py-20 md:py-28 bg-brand-black" data-id="element-275">
          <div className="max-w-4xl mx-auto px-6 lg:px-20" data-id="element-276">
            <motion.h2 {...fadeUp} className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-brand-white uppercase tracking-industrial mb-12 text-center" data-id="element-277">
              Ce înveți în cele trei zile
            </motion.h2>

            <div className="space-y-5 mb-12" data-id="element-278">
              {['Îți pui ordine în viață și îți setezi obiective clare pe toate ariile importante: familie, sănătate, bani, carieră și timp pentru tine.', 'Înțelegi cum funcționează banii, pe înțelesul tuturor, ca să iei decizii mai bune fără să te mai simți pierdut.', 'Îți faci un buget personal simplu, pe care chiar îl poți folosi acasă, ca să știi mereu unde se duc banii tăi.', 'Capeți curaj să vorbești în fața oamenilor, prin exerciții făcute prin joacă, alături de trupa Aparte cu accent pe e.', 'Cunoști oameni care gândesc ca tine și legi prietenii și colaborări care îți pot deschide uși noi după tabără.'].map((point, i) => <motion.div key={i} {...fadeUp} transition={{
              delay: i * 0.08
            }} className="flex items-start gap-4 bg-brand-grey border border-brand-border rounded-lg p-6 hover:border-brand-accent transition-colors duration-300" data-id="element-279">
                  <div className="w-8 h-8 rounded-full bg-brand-accent/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-280">
                    <CheckCircle2Icon className="w-5 h-5 text-brand-accent" data-id="element-281" />
                  </div>
                  <p className="font-body text-brand-white text-base md:text-lg leading-relaxed" data-id="element-282">
                    {point}
                  </p>
                </motion.div>)}
            </div>

            <div className="text-center" data-id="element-283">
              <CtaButton data-id="element-284">Rezervă-ți locul la tabără</CtaButton>
              <p className="font-body text-sm text-brand-dim mt-4" data-id="element-285">
                Totul se învață prin joacă, într-un loc liniștit de la munte.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 4 — HOW LIFE CHANGES */}
        <section className="py-20 md:py-28 bg-brand-dark" data-id="element-286">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20" data-id="element-287">
            <motion.h2 {...fadeUp} className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-brand-white uppercase tracking-industrial mb-12 text-center max-w-3xl mx-auto" data-id="element-288">
              Cum se schimbă viața ta după aceste trei zile
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-289">
              {[{
              icon: <TargetIcon className="w-8 h-8 text-brand-accent" data-id="element-290" />,
              title: 'Echilibru în viață',
              desc: 'Pleci cu un plan clar pentru familie, sănătate, muncă și timpul tău, ca să nu mai simți că alergi degeaba.'
            }, {
              icon: <WalletIcon className="w-8 h-8 text-brand-accent" data-id="element-291" />,
              title: 'Liniște cu banii',
              desc: 'Știi cât câștigi, cât cheltui și cât poți pune deoparte sau investi, ca banii să lucreze pentru tine, nu invers.'
            }, {
              icon: <MicIcon className="w-8 h-8 text-brand-accent" data-id="element-292" />,
              title: 'Curaj să te exprimi',
              desc: 'Vorbești mai ușor în fața oamenilor și ai o rețea nouă de prieteni și parteneri care te susțin mai departe.'
            }].map((card, i) => <motion.div key={i} {...fadeUp} transition={{
              delay: i * 0.15
            }} className="bg-brand-grey border border-brand-border rounded-lg p-8 hover:border-brand-accent transition-colors duration-300" data-id="element-293">
                  <div className="w-16 h-16 bg-brand-black rounded-lg border border-brand-border flex items-center justify-center mb-6" data-id="element-294">
                    {card.icon}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-white uppercase tracking-industrial mb-4" data-id="element-295">
                    {card.title}
                  </h3>
                  <p className="font-body text-brand-muted leading-relaxed" data-id="element-296">
                    {card.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 5 — WHO GUIDES YOU */}
        <section className="py-20 md:py-28 bg-brand-black" data-id="element-297">
          <div className="max-w-3xl mx-auto px-6 lg:px-20 text-center" data-id="element-298">
            <motion.div {...fadeUp} data-id="element-299">
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-brand-white uppercase tracking-industrial mb-10" data-id="element-300">
                Cine sunt oamenii care te ghidează în aceste trei zile
              </h2>

              <div className="bg-brand-grey border border-brand-border rounded-lg p-8 mb-10 text-left" data-id="element-301">
                <p className="font-body text-brand-muted text-lg leading-relaxed" data-id="element-302">
                  Tabăra este găzduită de Ionuț Drăgănescu — antreprenor, coach
                  și business mentor cu ani de experiență în dezvoltarea
                  oamenilor și a afacerilor. Alături de el, trupa Aparte cu
                  accent pe e te ajută să capeți curaj și să te exprimi liber,
                  prin exerciții făcute prin joacă.
                </p>
                <p className="font-sub italic text-brand-dim text-sm mt-4" data-id="element-303">
                  * Bio-ul complet al gazdei va fi confirmat de client.
                </p>
              </div>

              <CtaButton data-id="element-304">Rezervă-ți locul la tabără</CtaButton>
              <p className="font-body text-sm text-brand-dim mt-4" data-id="element-305">
                Trei zile la munte, alături de oameni care chiar vor să te
                ajute.
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6 — FOR WHO IS / IS NOT */}
        <section className="py-20 md:py-28 bg-brand-dark" data-id="element-306">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-20" data-id="element-307">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-308">
              {/* IS for */}
              <motion.div {...fadeUp} className="bg-brand-grey border border-brand-accent/40 rounded-lg p-8" data-id="element-309">
                <h3 className="font-heading font-bold text-xl md:text-2xl text-brand-white uppercase tracking-industrial mb-8" data-id="element-310">
                  Tabăra asta <span className="text-brand-accent" data-id="element-311">este</span>{' '}
                  pentru tine dacă...
                </h3>
                <ul className="space-y-5" data-id="element-312">
                  {['Ești antreprenor sau părinte și vrei să le arăți și copiilor tăi, prin joacă, cum se gândește sănătos la bani și la viață.', 'Muncești mult, dar simți că ți-ai pierdut echilibrul și vrei trei zile în care să pui totul la punct.', 'Vrei să înțelegi banii pe limba ta și să cunoști oameni faini, într-un loc frumos de la munte.'].map((p, i) => <li key={i} className="flex items-start gap-4" data-id="element-313">
                      <CheckCircle2Icon className="w-6 h-6 text-brand-accent shrink-0 mt-0.5" data-id="element-314" />
                      <span className="font-body text-brand-white leading-relaxed" data-id="element-315">
                        {p}
                      </span>
                    </li>)}
                </ul>
              </motion.div>

              {/* IS NOT for */}
              <motion.div {...fadeUp} transition={{
              delay: 0.15
            }} className="bg-brand-grey border border-brand-border rounded-lg p-8" data-id="element-316">
                <h3 className="font-heading font-bold text-xl md:text-2xl text-brand-white uppercase tracking-industrial mb-8" data-id="element-317">
                  Tabăra asta <span className="text-brand-dim" data-id="element-318">NU este</span>{' '}
                  pentru tine dacă...
                </h3>
                <ul className="space-y-5" data-id="element-319">
                  {['Cauți o schemă de îmbogățire rapidă sau sfaturi de investiții garantate. Aici vorbim despre lucruri reale, pas cu pas.', 'Vrei doar teorie și cursuri formale, stând în bancă. La noi se învață prin joacă și prin mișcare.', 'Nu ești dispus să te deschizi, să participi și să te joci alături de ceilalți timp de trei zile.'].map((p, i) => <li key={i} className="flex items-start gap-4" data-id="element-320">
                      <XCircleIcon className="w-6 h-6 text-brand-dim shrink-0 mt-0.5" data-id="element-321" />
                      <span className="font-body text-brand-muted leading-relaxed" data-id="element-322">
                        {p}
                      </span>
                    </li>)}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 8 — FINAL CTA (Testimonials section 7 hidden — no testimonials yet) */}
        <section className="py-24 md:py-32 bg-brand-black industrial-texture relative overflow-hidden" data-id="element-323">
          <img src="https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=1400&q=60&auto=format&fit=crop" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" data-id="element-324" />
          <div className="absolute inset-0 bg-brand-black/85 z-0" data-id="element-325" />

          <div className="max-w-[1280px] mx-auto px-6 lg:px-20 relative z-10" data-id="element-326">
            <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center" data-id="element-327">
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-brand-white uppercase tracking-industrial mb-6 leading-tight" data-id="element-328">
                Trei zile care îți pot schimba relația cu banii și cu viața
              </h2>
              <div className="flex items-baseline justify-center gap-3 mb-10" data-id="element-329">
                <span className="font-heading font-black text-3xl text-brand-accent" data-id="element-330">
                  500 lei
                </span>
                <span className="font-body text-brand-dim line-through text-lg" data-id="element-331">
                  1200 lei
                </span>
                <span className="font-body text-brand-muted text-sm" data-id="element-332">
                  / persoană
                </span>
              </div>
              <CtaButton large data-id="element-333">Rezervă-ți locul la tabără</CtaButton>
              <p className="font-body text-base text-brand-muted mt-6 max-w-xl mx-auto" data-id="element-334">
                Locurile sunt limitate, iar banii pe care îi plătești merg către
                o cauză bună.
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <LegalFooter data-id="element-335" />
    </div>;
}